import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import homeStyles from '../../../../constants/homeStyles';
import { useDefineUserRole, useFormatMessage } from '../../../../hooks';

import { ICategoryItem } from '../interfaces';
import { IUser } from '../../../../interfaces/user';
import { useStylesCategoryForm } from '../hooks/useStyles';
import { ButtonWithLoader } from '../../../../components/other/Buttons';
// import { homepage } from '../../../../../../package.json';

interface IProps {
  formik: any;
  id: string;
  editLoading: boolean;
  categories?: ICategoryItem[];
  me?: IUser;
}

const levelsSubCategories = [1, 2, 3, 4, 5];

export const CategoryForm: React.FC<IProps> = ({
  formik,
  id,
  editLoading,
  categories,
  me,
}) => {
  const fm = useFormatMessage();
  const navigate = useNavigate();
  const classes1 = useStylesCategoryForm();
  const { classes } = classes1;
  const homeClasses = homeStyles();
  const [subCategories, setSubCategories] = useState<ICategoryItem[][]>([]);
  const isManager = useDefineUserRole(me, 'ROLE_MANAGER');

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue } =
    formik;

  const getSubCategories = useCallback(
    (level: number) => {
      return subCategories[level];
    },
    [subCategories]
  );

  useEffect(() => {
    if (values.mainCategoryId && categories) {
      const newCategories = categories.find(
        item => item.id?.toString() === values.mainCategoryId?.toString()
      );
      if (values.mainCategoryId === -1) {
        setFieldValue(`categoryId_1`, '');
        setFieldValue(`categoryId_2`, '');
        setFieldValue(`categoryId_3`, '');
        setFieldValue(`categoryId_4`, '');
        setFieldValue(`categoryId_5`, '');
      }
      if (newCategories && newCategories.children) {
        if (
          subCategories[1] &&
          subCategories[1].length > 0 &&
          subCategories[1][0]?.id !== newCategories.children[0]?.id
        ) {
          levelsSubCategories.forEach(level => setFieldValue(`categoryId_${level}`, ''));
        }
        const subCategoriesLvl1 = newCategories.children;
        const subCategoriesLvl2: ICategoryItem | undefined =
          values.categoryId_1 &&
          subCategoriesLvl1?.length &&
          newCategories.children.find(
            item => item.id?.toString() === values.categoryId_1?.toString()
          );
        const subCategoriesLvl3: ICategoryItem | undefined =
          values.categoryId_2 &&
          subCategoriesLvl2?.children?.length &&
          subCategoriesLvl2.children.find(
            item => item.id?.toString() === values.categoryId_2?.toString()
          );
        const subCategoriesLvl4: ICategoryItem | undefined =
          values.categoryId_3 &&
          subCategoriesLvl3?.children?.length &&
          subCategoriesLvl3.children.find(
            item => item.id?.toString() === values.categoryId_3?.toString()
          );
        const subCategoriesLvl5: ICategoryItem | undefined =
          values.categoryId_4 &&
          subCategoriesLvl4?.children?.length &&
          subCategoriesLvl4.children.find(
            item => item.id?.toString() === values.categoryId_4?.toString()
          );
        if (subCategoriesLvl1.length === 0) {
          setFieldValue(`categoryId_1`, '');
          setFieldValue(`categoryId_2`, '');
          setFieldValue(`categoryId_3`, '');
          setFieldValue(`categoryId_4`, '');
          setFieldValue(`categoryId_5`, '');
        }
        if (subCategoriesLvl2?.children?.length === 0) {
          setFieldValue(`categoryId_2`, '');
          setFieldValue(`categoryId_3`, '');
          setFieldValue(`categoryId_4`, '');
          setFieldValue(`categoryId_5`, '');
        }
        if (subCategoriesLvl3?.children?.length === 0) {
          setFieldValue(`categoryId_3`, '');
          setFieldValue(`categoryId_4`, '');
          setFieldValue(`categoryId_5`, '');
        }
        if (subCategoriesLvl4?.children?.length === 0) {
          setFieldValue(`categoryId_4`, '');
          setFieldValue(`categoryId_5`, '');
        }
        if (subCategoriesLvl5?.children?.length === 0) {
          setFieldValue(`categoryId_5`, '');
        }
        setSubCategories(state => {
          const newArr = [...state];
          newArr[1] = subCategoriesLvl1.length ? subCategoriesLvl1 : [];
          newArr[2] = subCategoriesLvl2?.children?.length ? subCategoriesLvl2?.children : [];
          newArr[3] = subCategoriesLvl3?.children?.length ? subCategoriesLvl3?.children : [];
          newArr[4] = subCategoriesLvl4?.children?.length ? subCategoriesLvl4?.children : [];
          newArr[5] = subCategoriesLvl5?.children?.length ? subCategoriesLvl5?.children : [];
          return newArr;
        });
      } else {
        setSubCategories([]);
      }
    } else {
      setSubCategories([]);
    }
  }, [
    categories,
    values.mainCategoryId,
    values.categoryId_1,
    values.categoryId_2,
    values.categoryId_3,
    values.categoryId_4,
    values.categoryId_5,
  ]);

  return (
    <>
      <div className={classes.formEdit}>
        <div className={classes.cardEdit}>
          <TextField
            type='text'
            label={`${fm('CATEGORY.EDIT.NAME')}`}
            margin='normal'
            name='name'
            value={values.name}
            variant='outlined'
            onBlur={handleBlur}
            onChange={handleChange}
            helperText={touched.name && errors.name}
            error={Boolean(touched.name && errors.name)}
            disabled={isManager || false}
          />

          <TextField
            select
            label={fm('CATEGORY.EDIT.PARENT')}
            margin='normal'
            name='mainCategoryId'
            value={values.mainCategoryId || ''}
            variant='outlined'
            onBlur={handleBlur('mainCategoryId')}
            onChange={handleChange}
            disabled={isManager || false}
            helperText={touched.mainCategoryId && errors.mainCategoryId}
            error={Boolean(touched.mainCategoryId && errors.mainCategoryId)}
          >
            <MenuItem color='secondary' key='-1' value={-1}>
              {fm('CATEGORY.EDIT.ROOT')}
            </MenuItem>
            {categories &&
              categories.map(item => (
                <MenuItem
                  color='secondary'
                  key={item.id?.toString()}
                  value={item.id?.toString()}
                >
                  {item.name}
                </MenuItem>
              ))}
          </TextField>

          {levelsSubCategories.map(level => {
            const subCategories = getSubCategories(level);
            if (subCategories && subCategories?.length > 0) {
              return (
                <TextField
                  select
                  label={`${fm('PRODUCT.EDIT.SUBCATEGORY')} ${level}`}
                  margin='normal'
                  name={`categoryId_${level}`}
                  value={values[`categoryId_${level}`] || ''}
                  variant='outlined'
                  onBlur={handleBlur(`categoryId_${level}`)}
                  onChange={e => {
                    if (e.target.value === '-1') {
                      // eslint-disable-next-line no-plusplus
                      for (let i = level + 1; i <= 5; i++) {
                        setFieldValue(`categoryId_${i}`, '');
                      }
                    }
                    setFieldValue(
                      `categoryId_${level}`,
                      e.target.value === '-1' ? '' : e.target.value
                    );
                  }}
                  disabled={!values.mainCategoryId || isManager || false}
                  helperText={touched[`categoryId_${level}`] && errors.categoryId}
                  error={Boolean(touched[`categoryId_${level}`] && errors.categoryId)}
                >
                  <MenuItem color='secondary' key='-1' value='-1'>
                    -
                  </MenuItem>
                  {subCategories.length > 0 &&
                    subCategories.map(item => (
                      <MenuItem key={item.id?.toString()} value={item.id?.toString()}>
                        {item.name}
                      </MenuItem>
                    ))}
                </TextField>
              );
            }
            return <></>;
          })}

          <TextField
            type='text'
            label={`${fm('CATEGORY.EDIT.DESCRIPTION')}`}
            margin='normal'
            name='description'
            value={values.description?.replace(/<\/?[^>]+(>|$)/g, '')}
            variant='outlined'
            onBlur={handleBlur}
            onChange={handleChange}
            multiline
            rows={4}
          />

          <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <FormControlLabel
              color='secondary'
              control={
                <Switch
                  className={classes.switchs}
                  size='small'
                  name='active'
                  checked={values.active}
                  onChange={handleChange}
                  color='secondary'
                />
              }
              label={fm('CATEGORY.EDIT.ACTIVE')}
            />

            <FormControlLabel
              color='secondary'
              control={
                <Switch
                  className={homeClasses.classes.switch}
                  size='small'
                  name='favorite'
                  checked={values.favorite || false}
                  onChange={handleChange}
                  color='secondary'
                />
              }
              label={fm('FAVORITE')}
            />
          </Box>

          <div className={classes.actions}>
            <Button
              onClick={() => navigate(-1)}
              // className={classes.buttons}
              style={{ marginRight: 16 }}
              variant='contained'
              color='success'
            >
              {fm('CATEGORY.BUTTON.CANCEL')}
            </Button>

            <ButtonWithLoader
              disabled={editLoading}
              loading={editLoading}
              onPress={handleSubmit}
            >
              {+id ? fm('CATEGORY.BUTTON.EDIT') : fm('CATEGORY.BUTTON.ADD')}
            </ButtonWithLoader>
          </div>
        </div>
      </div>
    </>
  );
};
